import { useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, ArrowLeft, XCircle } from "lucide-react";
import bgDecorative from "@/assets/onboarding/bg-decorative.svg";
import logo from "@/assets/Logo.png";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
export default function ResetPassword() {
  const [, params] = useRoute("/reset-password/:token");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const token = params?.token || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please ensure both passwords are identical",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, newPassword }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || "Failed to reset password");
      }

      const data = await response.json();

      setResetSuccess(true);
      toast({
        title: "Password Reset Successful",
        description: data.message,
      });

      setTimeout(() => {
        setLocation("/login");
      }, 3000);
    } catch (error) {
      toast({
        title: "Reset Failed",
        description: error instanceof Error ? error.message : "Failed to reset password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{
        backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-[560px] bg-white rounded-3xl shadow-[0px_8px_10px_-6px_rgba(0,0,0,0.10)] shadow-xl outline outline-1 outline-offset-[-1px] outline-slate-200 overflow-hidden">
          <div className="p-16 flex flex-col items-center gap-8">
            <img
              className="w-24 h-9 object-contain cursor-pointer"
              src={logo}
              alt="180Pi"
              onClick={() => setLocation("/")}
            />

            <div className="w-full flex flex-col items-center gap-2">
              <h1 className="text-center text-3xl font-extrabold leading-9" style={{ color: "#00444C" }}>
                {"Set New Password"}
              </h1>
              <p className="text-center text-slate-600 text-base font-normal leading-6">
                {resetSuccess
                  ? "Your password has been reset successfully"
                  : "Enter your new password below"}
              </p>
            </div>

            {resetSuccess ? (
              <div className="w-full flex flex-col gap-5">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-6 text-center">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
                  <p className="mt-4 text-lg font-semibold leading-7 text-slate-800">
                    {"Password Reset Complete!"}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {"Redirecting you to login..."}
                  </p>
                </div>

                <Link
                  href="/login"
                  className="w-full px-4 py-3 rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex justify-center items-center text-white text-sm font-bold leading-5 transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#00444C" }}
                >
                  {"Go to Login"}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="w-full flex flex-col gap-5">
                {!token && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                      <div>
                        <p className="text-sm font-semibold leading-5 text-red-800">
                          {"Invalid Reset Link"}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-red-700">
                          {"This password reset link is invalid or incomplete. Please request a new reset link."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="newPassword" className="text-slate-700 text-sm font-semibold leading-5">
                    {"New Password"}
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isLoading || !token}
                    className="px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-slate-200 text-sm text-slate-700 placeholder:text-gray-500"
                    minLength={6}
                  />
                  <p className="text-xs leading-5 text-slate-500">
                    {"Must be at least 6 characters"}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700 text-sm font-semibold leading-5">
                    {"Confirm New Password"}
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading || !token}
                    className="px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-slate-200 text-sm text-slate-700 placeholder:text-gray-500"
                    minLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !token}
                  className="w-full px-4 py-3 rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex justify-center items-center gap-2 text-white text-sm font-bold leading-5 disabled:opacity-50 cursor-pointer transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#00444C" }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {"Resetting Password..."}
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>

                <Link
                  href="/login"
                  className="w-full px-4 py-3 rounded-2xl flex items-center justify-center gap-2 text-sm font-medium leading-5 transition-colors hover:bg-slate-50"
                  style={{ color: "#00444C" }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {"Back to Login"}
                </Link>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
